export interface ContentSegment {
  type: 'text' | 'table' | 'codeblock' | 'chart';
  content: string;
  startIndex: number;
  endIndex: number;
  language?: string; // For code blocks, store the language identifier
  chartType?: 'candlestick' | 'line'; // For charts, store the chart type
  dataId?: string; // For charts, store the data ID
  ticker?: string; // For charts, store the ticker symbol
  studies?: string; // For charts, store comma-separated technical indicators
}

export interface TableData {
  headers: string[];
  rows: string[][];
  alignment?: ('left' | 'center' | 'right')[];
}

export interface SortState {
  column: number | null;
  direction: 'asc' | 'desc' | null;
}

export type SortDirection = 'asc' | 'desc' | null;

/**
 * Detects the data type of a column based on its values
 */
export function detectColumnType(values: string[]): 'number' | 'date' | 'text' {
  // Filter out empty values and common placeholders
  const nonEmptyValues = values.filter(val =>
    val.trim() !== '' && val.trim() !== '-' && val.trim() !== 'N/A'
  );

  if (nonEmptyValues.length === 0) return 'text';

  // Check for numbers (including percentages and currency)
  const numberPattern = /^[+\-]?\$?\d+(?:,\d{3})*(?:\.\d+)?%?$/;
  const numberCount = nonEmptyValues.filter(val => numberPattern.test(val.trim())).length;

  // Check for dates (various formats)
  const datePattern = /^\d{4}-\d{2}-\d{2}$|^\d{1,2}\/\d{1,2}\/\d{4}$|^\d{1,2}-\d{1,2}-\d{4}$/;
  const dateCount = nonEmptyValues.filter(val => datePattern.test(val.trim())).length;

  // If 80% or more values match a pattern, consider it that type
  const threshold = nonEmptyValues.length * 0.8;

  if (numberCount >= threshold) return 'number';
  if (dateCount >= threshold) return 'date';
  return 'text';
}

/**
 * Parses a value for sorting based on detected type
 */
export function parseValueForSorting(value: string, type: 'number' | 'date' | 'text'): number | Date | string {
  const trimmed = value.trim();

  // Handle empty/placeholder values
  if (trimmed === '' || trimmed === '-' || trimmed === 'N/A') {
    return type === 'number' ? -Infinity : type === 'date' ? new Date(0) : '';
  }

  switch (type) {
    case 'number':
      // Remove currency symbols, commas, percentage signs
      const cleanNumber = trimmed.replace(/[\$,%]/g, '');
      const parsed = parseFloat(cleanNumber);
      return isNaN(parsed) ? -Infinity : parsed;

    case 'date':
      const date = new Date(trimmed);
      return isNaN(date.getTime()) ? new Date(0) : date;

    case 'text':
    default:
      return trimmed.toLowerCase();
  }
}

/**
 * Sorts table data by specified column and direction
 */
export function sortTableData(
  tableData: TableData,
  columnIndex: number,
  direction: 'asc' | 'desc'
): TableData {
  if (columnIndex < 0 || columnIndex >= tableData.headers.length) {
    return tableData;
  }

  // Detect column type
  const columnValues = tableData.rows.map(row => row[columnIndex] || '');
  const columnType = detectColumnType(columnValues);

  // Create sorted rows with original indices to maintain stability
  const rowsWithIndex = tableData.rows.map((row, index) => ({ row, originalIndex: index }));

  rowsWithIndex.sort((a, b) => {
    const valueA = parseValueForSorting(a.row[columnIndex] || '', columnType);
    const valueB = parseValueForSorting(b.row[columnIndex] || '', columnType);

    let comparison = 0;

    if (valueA < valueB) comparison = -1;
    else if (valueA > valueB) comparison = 1;
    else comparison = a.originalIndex - b.originalIndex; // Maintain stable sort

    return direction === 'desc' ? -comparison : comparison;
  });

  return {
    ...tableData,
    rows: rowsWithIndex.map(item => item.row)
  };
}

export interface ReferenceData {
  name: string;
  url?: string;
}

export interface ChartData {
  type: 'candlestick' | 'line';
  dataId: string;
  ticker?: string;
  studies?: string;
}

interface TableMatch {
  content: string;
  index: number;
}

interface LineWithOffset {
  text: string;
  start: number;
  fullEnd: number;
}

function getLinesWithOffsets(content: string): LineWithOffset[] {
  if (!content) return [];

  const lines: LineWithOffset[] = [];
  let cursor = 0;

  while (cursor < content.length) {
    const newlineIndex = content.indexOf('\n', cursor);

    if (newlineIndex === -1) {
      let lineText = content.slice(cursor);
      if (lineText.endsWith('\r')) {
        lineText = lineText.slice(0, -1);
      }

      lines.push({
        text: lineText,
        start: cursor,
        fullEnd: content.length,
      });
      break;
    }

    let lineText = content.slice(cursor, newlineIndex);
    if (lineText.endsWith('\r')) {
      lineText = lineText.slice(0, -1);
    }

    lines.push({
      text: lineText,
      start: cursor,
      fullEnd: newlineIndex + 1,
    });

    cursor = newlineIndex + 1;
  }

  return lines;
}

function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|') || !trimmed.endsWith('|')) {
    return false;
  }

  return trimmed.indexOf('|', 1) !== -1;
}

function isTableSeparatorRow(line: string): boolean {
  if (!isTableRow(line)) {
    return false;
  }

  const cells = line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map(cell => cell.trim());

  if (cells.length === 0) {
    return false;
  }

  return cells.every(cell => /^:?-{3,}:?$/.test(cell));
}

function findCompleteTables(content: string): TableMatch[] {
  if (!content.includes('|')) {
    return [];
  }

  const lines = getLinesWithOffsets(content);
  const matches: TableMatch[] = [];

  for (let i = 0; i < lines.length - 2; i++) {
    if (!isTableRow(lines[i].text) || !isTableSeparatorRow(lines[i + 1].text)) {
      continue;
    }

    let lastRowIndex = i + 1;

    for (let j = i + 2; j < lines.length; j++) {
      if (!isTableRow(lines[j].text)) {
        break;
      }
      lastRowIndex = j;
    }

    if (lastRowIndex === i + 1) {
      continue;
    }

    matches.push({
      content: content.slice(lines[i].start, lines[lastRowIndex].fullEnd),
      index: lines[i].start,
    });

    i = lastRowIndex;
  }

  return matches;
}

/**
 * Detects complete markdown tables in text content
 */
export function detectCompleteTables(content: string): TableMatch[] {
  return findCompleteTables(content);
}

/**
 * Checks if content contains any complete markdown tables
 */
export function hasCompleteTables(content: string): boolean {
  if (!content.includes('|')) {
    return false;
  }

  const lines = getLinesWithOffsets(content);

  for (let i = 0; i < lines.length - 2; i++) {
    if (!isTableRow(lines[i].text) || !isTableSeparatorRow(lines[i + 1].text)) {
      continue;
    }

    if (isTableRow(lines[i + 2].text)) {
      return true;
    }
  }

  return false;
}

/**
 * Regex pattern to match reference tags
 * Matches tags like: <reference name="your_reference_name" url="your_reference_url_optionally" />
 * or: <reference name="your_reference_name" />
 */
const REFERENCE_TAG_REGEX = /<reference\s+name="([^"]+)"(?:\s+url="([^"]*)")?\s*\/>/g;

/**
 * Detects reference tags in text content
 */
export function detectReferences(content: string): RegExpMatchArray[] {
  return Array.from(content.matchAll(REFERENCE_TAG_REGEX));
}

/**
 * Parses a reference tag string into structured data
 */
export function parseReference(referenceTag: string): ReferenceData | null {
  const match = referenceTag.match(/<reference\s+name="([^"]+)"(?:\s+url="([^"]*)")?\s*\/>/);
  if (!match) return null;

  const name = match[1];
  const url = match[2] || undefined;

  return { name, url };
}

/**
 * Regex pattern to match chart tags
 * Matches tags like: <chart type="candlestick" data_id="uuid" ticker="TSLA" />
 * or: <chart type="line" data_id="uuid" />
 */
const CHART_TAG_REGEX = /<chart\s+type="(candlestick|line)"\s+data_id="([^"]+)"(?:\s+ticker="([^"]+)")?\s*\/>/g;

/**
 * Detects chart tags in text content
 */
export function detectCharts(content: string): RegExpMatchArray[] {
  return Array.from(content.matchAll(CHART_TAG_REGEX));
}

/**
 * Parses a chart tag string into structured data
 */
export function parseChart(chartTag: string): ChartData | null {
  // Extract attributes using individual patterns to handle any order
  const typeMatch = chartTag.match(/type="(candlestick|line)"/);
  const dataIdMatch = chartTag.match(/data_id="([^"]+)"/);
  const tickerMatch = chartTag.match(/ticker="([^"]*)"/);
  const studiesMatch = chartTag.match(/studies="([^"]*)"/);

  if (!typeMatch || !dataIdMatch) return null;

  const type = typeMatch[1] as 'candlestick' | 'line';
  const dataId = dataIdMatch[1];
  const ticker = tickerMatch ? tickerMatch[1] : undefined;
  const studies = studiesMatch ? studiesMatch[1] : undefined;

  return { type, dataId, ticker, studies };
}

/**
 * Splits content into segments of text, complete tables, code blocks, and charts
 * Returns array of segments in order they appear
 * Note: References are now handled inline within text segments
 */
export function segmentContent(content: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  const tableMatches = detectCompleteTables(content);
  const codeBlockMatches = detectCodeBlocks(content);
  const chartMatches = detectCharts(content);

  // Combine all matches and sort by position
  const allMatches: Array<{
    type: 'table' | 'codeblock' | 'chart';
    match: TableMatch | RegExpMatchArray;
    startIndex: number;
    endIndex: number;
  }> = [
    ...tableMatches.map(match => ({
      type: 'table' as const,
      match,
      startIndex: match.index,
      endIndex: match.index + match.content.length
    })),
    ...codeBlockMatches.map(match => ({
      type: 'codeblock' as const,
      match,
      startIndex: match.index!,
      endIndex: match.index! + match[0].length
    })),
    ...chartMatches.map(match => ({
      type: 'chart' as const,
      match,
      startIndex: match.index!,
      endIndex: match.index! + match[0].length
    }))
  ].sort((a, b) => a.startIndex - b.startIndex);

  if (allMatches.length === 0) {
    // No tables or code blocks found, return entire content as text
    segments.push({
      type: 'text',
      content: content,
      startIndex: 0,
      endIndex: content.length
    });
    return segments;
  }

  let currentIndex = 0;

  for (const matchInfo of allMatches) {
    const { type, match, startIndex, endIndex } = matchInfo;

    // Add text segment before this match (if any)
    if (startIndex > currentIndex) {
      const textContent = content.slice(currentIndex, startIndex);
      if (textContent.trim()) {
        segments.push({
          type: 'text',
          content: textContent,
          startIndex: currentIndex,
          endIndex: startIndex
        });
      }
    }

    // Add the match segment
    if (type === 'table') {
      const tableMatch = match as TableMatch;
      segments.push({
        type: 'table',
        content: tableMatch.content,
        startIndex: startIndex,
        endIndex: endIndex
      });
    } else if (type === 'codeblock') {
      const regexMatch = match as RegExpMatchArray;
      const { language } = extractCodeBlockContent(regexMatch);
      segments.push({
        type: 'codeblock',
        content: regexMatch[0],
        startIndex: startIndex,
        endIndex: endIndex,
        language
      });
    } else { // chart
      const regexMatch = match as RegExpMatchArray;
      const chartData = parseChart(regexMatch[0]);
      if (chartData) {
        segments.push({
          type: 'chart',
          content: regexMatch[0],
          startIndex: startIndex,
          endIndex: endIndex,
          chartType: chartData.type,
          dataId: chartData.dataId,
          ticker: chartData.ticker,
          studies: chartData.studies
        });
      }
    }

    currentIndex = endIndex;
  }

  // Add remaining text after last match (if any)
  if (currentIndex < content.length) {
    const remainingContent = content.slice(currentIndex);
    if (remainingContent.trim()) {
      segments.push({
        type: 'text',
        content: remainingContent,
        startIndex: currentIndex,
        endIndex: content.length
      });
    }
  }

  return segments;
}

/**
 * Checks if a table appears to be incomplete (for streaming scenarios)
 * This looks for patterns that suggest a table is being written
 */
export function hasIncompleteTable(content: string): boolean {
  const lines = content.split('\n');
  const lastFewLines = lines.slice(-5); // Check last 5 lines

  // Look for table-like patterns that might be incomplete
  for (const line of lastFewLines) {
    // Line starts with | but doesn't end with | (might be incomplete)
    if (line.trim().startsWith('|') && !line.trim().endsWith('|')) {
      return true;
    }
    // Line looks like a separator row but incomplete
    if (line.includes('|') && line.includes('-') && !line.trim().endsWith('|')) {
      return true;
    }
  }

  return false;
}

/**
 * Parses a markdown table string into structured data
 */
export function parseMarkdownTable(markdownTable: string): TableData {
  const lines = markdownTable.trim().split('\n');

  if (lines.length < 3) {
    throw new Error('Invalid markdown table: minimum 3 lines required (header, separator, data)');
  }

  // Parse header row
  const headerLine = lines[0].trim();
  const headers = parseTableRow(headerLine);

  // Parse alignment row
  const alignmentLine = lines[1].trim();
  const alignment = parseTableAlignment(alignmentLine);

  // Parse data rows
  const dataLines = lines.slice(2);
  const rows = dataLines.map(line => parseTableRow(line.trim()));

  return {
    headers,
    rows,
    alignment
  };
}

/**
 * Parses a single table row (removes leading/trailing pipes and splits by pipes)
 */
function parseTableRow(rowString: string): string[] {
  // Remove leading and trailing pipes, then split by pipes
  const cleaned = rowString.replace(/^\||\|$/g, '');
  const cells = cleaned.split('|').map(cell => cell.trim());
  return cells;
}

/**
 * Parses the alignment row to determine column alignments
 */
function parseTableAlignment(alignmentString: string): ('left' | 'center' | 'right')[] {
  const cleaned = alignmentString.replace(/^\||\|$/g, '');
  const alignmentCells = cleaned.split('|').map(cell => cell.trim());

  return alignmentCells.map(cell => {
    if (cell.startsWith(':') && cell.endsWith(':')) {
      return 'center';
    } else if (cell.endsWith(':')) {
      return 'right';
    } else {
      return 'left';
    }
  });
}

/**
 * Converts a markdown table string to TableData
 * Wrapper function that handles errors gracefully
 */
export function parseMarkdownTableSafe(markdownTable: string): TableData | null {
  try {
    return parseMarkdownTable(markdownTable);
  } catch (error) {
    console.warn('Failed to parse markdown table:', error);
    return null;
  }
}

/**
 * Regex pattern to match code blocks with triple backticks
 * Captures optional language identifier and code content
 */
const CODE_BLOCK_REGEX = /^```(\w*)\s*\n([\s\S]*?)\n```$/gm;

/**
 * Detects code blocks in content and extracts their information
 */
export function detectCodeBlocks(content: string): RegExpMatchArray[] {
  return Array.from(content.matchAll(CODE_BLOCK_REGEX));
}

/**
 * Checks if a string contains markdown table syntax
 * More lenient than the complete table regex - just checks for basic table patterns
 */
export function looksLikeTable(content: string): boolean {
  const trimmed = content.trim();
  const lines = trimmed.split('\n');

  // Need at least 3 lines for a table (header, separator, data)
  if (lines.length < 3) return false;

  // Check if we have pipe-separated rows
  const pipedLines = lines.filter(line => line.includes('|'));
  if (pipedLines.length < 3) return false;

  // Check for separator row pattern (should contain dashes and pipes)
  const hasSeparator = lines.some(line =>
    line.includes('|') &&
    line.includes('-') &&
    /^\s*\|?[\s-:|]+\|?\s*$/.test(line)
  );

  return hasSeparator;
}

/**
 * Extracts and cleans content from a code block
 */
export function extractCodeBlockContent(codeBlockMatch: RegExpMatchArray): {
  language: string;
  content: string;
} {
  const language = codeBlockMatch[1] || ''; // Language identifier (empty if none)
  const content = codeBlockMatch[2].trim(); // Content without leading/trailing whitespace

  return { language, content };
}
